import { Error } from "@/components/admin/error";
import { setThemeColor, syncThemeColor } from "@/components/admin/theme-color";
import { useTheme } from "@/components/admin/use-theme";
import { Notification } from "@/components/admin/notification";
import { Skeleton } from "@/components/ui/skeleton";
import { useVisualViewport } from "@/hooks/useVisualViewport";
import { cn } from "@/lib/utils";
import "@/styles/keyboard.css";
import {
  Suspense,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useLocation } from "react-router";

import { DataImportProvider } from "../dataImport/DataImportProvider";
import { useConfigurationLoader } from "../root/useConfigurationLoader";
import { MobileChromeContext } from "./MobileChrome";
import { useMobileWindowState, type MobileWindowId } from "./MobileWindowState";
import { MobileNavigation } from "./MobileNavigation";
import { MobileScrollEdges } from "./MobileScrollEdges";
import { MobileTopBar } from "./MobileTopBar";
import { isDesktopPagePath } from "./mobileModules";
import { SplashIdentityGate } from "../splash/SplashGates";
import { PullToRefresh } from "./PullToRefresh";
import { NotificationsWindow } from "./NotificationsWindow";
import { UserSettingsSheet } from "./UserSettingsSheet";
import { useSettingsRouteSheet } from "./useSettingsRouteSheet";
import { useWindowHistory } from "./useWindowHistory";
import { AgendaWindow } from "../mobile/AgendaWindow";

export const MobileLayout = ({ children }: { children: ReactNode }) => {
  useConfigurationLoader();
  useVisualViewport();
  const { pathname } = useLocation();
  // Lives above <Admin>: a locale change remounts this layout.
  const { active: activeWindow, setActive: setActiveWindow } =
    useMobileWindowState();
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme } = useTheme();
  const [scrollLock, setScrollLock] = useState(0);

  const consumeHistoryEntry = useWindowHistory(activeWindow, setActiveWindow);

  const openWindow = useCallback(
    (id: MobileWindowId) => {
      setScrollLock(window.scrollY);
      setMenuOpen(false);
      setActiveWindow(id);
    },
    [setActiveWindow],
  );

  const closeWindow = useCallback(() => {
    consumeHistoryEntry();
    setActiveWindow(null);
  }, [consumeHistoryEntry, setActiveWindow]);

  const openAccount = useCallback(() => openWindow("account"), [openWindow]);
  useSettingsRouteSheet(openAccount);

  // While a window is up the page stays where it is (no transform or
  // position swap on the app root, which left iOS with black bands and stale
  // hit areas on the fixed tab bar once it closed): only its scrolling stops.
  // The canvas under it and the status bar take the window's colour.
  const windowOpen = activeWindow != null;
  useLayoutEffect(() => {
    if (!windowOpen) return;
    const root = document.documentElement;
    const previous = root.style.overflow;
    root.style.overflow = "hidden";
    root.classList.add("morph-window-open");
    // After the theme provider's own effect, which repaints theme-color.
    const frame = requestAnimationFrame(() => {
      const canvas = getComputedStyle(root)
        .getPropertyValue("--morph-window-canvas")
        .trim();
      if (canvas) setThemeColor(canvas);
    });
    return () => {
      cancelAnimationFrame(frame);
      root.style.overflow = previous;
      root.classList.remove("morph-window-open");
      syncThemeColor(theme);
    };
  }, [windowOpen, theme]);

  const chrome = useMemo(
    () => ({
      activeWindow,
      openWindow,
      closeWindow,
      menuOpen,
      setMenuOpen,
      scrollLock,
    }),
    [activeWindow, openWindow, closeWindow, menuOpen, scrollLock],
  );

  return (
    <MobileChromeContext.Provider value={chrome}>
      <DataImportProvider>
        <div className="mobile-chrome-root min-h-dvh bg-background">
          <PullToRefresh />
          <ErrorBoundary FallbackComponent={Error}>
            <Suspense
              fallback={<Skeleton className="h-12 w-12 rounded-full" />}
            >
              <div
                data-splash-reveal
                className={cn(
                  "pt-[var(--mobile-content-top)] pb-[var(--mobile-content-bottom)]",
                  isDesktopPagePath(pathname) && "px-4",
                )}
              >
                {children}
              </div>
            </Suspense>
          </ErrorBoundary>
          <MobileScrollEdges />
          <MobileTopBar />
          <MobileNavigation />
          <Notification
            mobileOffset={{
              bottom:
                "calc(var(--mobile-pill) + var(--mobile-nav-bottom) + 12px)",
            }}
          />
        </div>
        <UserSettingsSheet />
        <AgendaWindow />
        <NotificationsWindow />
        <SplashIdentityGate />
      </DataImportProvider>
    </MobileChromeContext.Provider>
  );
};
